import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Map as MapIcon, ChevronRight, Share2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import BeachCard from "@/components/BeachCard";
import BeachMap from "@/components/BeachMap";
import Footer from "@/components/Footer";
import SeoHead from "@/components/SeoHead";
import { getBeaches } from "@/lib/api";
import { toast } from "sonner";

const CityPage = () => {
  const { stateSlug, citySlug } = useParams();
  const [beaches, setBeaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cityName, setCityName] = useState("");
  const [stateName, setStateName] = useState("");
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await getBeaches({
          state: stateSlug,
          city: citySlug,
          per_page: 50,
        });

        setBeaches(data.beaches || []);
        setTotal(data.total || 0);

        if (data.beaches?.[0]) {
          setCityName(data.beaches[0].city);
          setStateName(data.beaches[0].state);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [stateSlug, citySlug]);

  const formatName = (slug) => {
    return slug
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const displayCityName = cityName || formatName(citySlug);
  const displayStateName = stateName || formatName(stateSlug);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Beach Access in ${displayCityName}, ${displayStateName}`,
          text: `Find ${total} public beach access points in ${displayCityName}`,
          url: url,
        });
      } catch (err) {
        console.log("Share cancelled");
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!");
    }
  };

  const BEACH_IMAGE = "/images/hero/beach-default.jpg";

  // Get city image
  const getCityImage = () => {
    return BEACH_IMAGE;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-amber-50" data-testid="city-page">
      <SeoHead
        title={`Public Beach Access in ${displayCityName}, ${displayStateName}`}
        description={`Explore ${total} free public beach access points in ${displayCityName}, ${displayStateName}. View beach locations on a map, check for parking, restrooms, showers, and wheelchair accessibility. Read visitor reviews and get driving directions to each beach entrance.`}
        path={`/state/${stateSlug}/${citySlug}`}
      />
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4 overflow-x-auto">
            <Link to="/" className="hover:text-[#0EA5E9] whitespace-nowrap">Home</Link>
            <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
            <Link to="/search" className="hover:text-[#0EA5E9] whitespace-nowrap">All States</Link>
            <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
            <Link to={`/state/${stateSlug}`} className="hover:text-[#0EA5E9] whitespace-nowrap">{displayStateName}</Link>
            <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
            <span className="text-gray-800 font-medium whitespace-nowrap">{displayCityName}</span>
          </nav>

          {/* Title Row */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-gray-800 font-['Outfit'] tracking-tight">
                🏖️ Beach Access in {displayCityName}
              </h1>
              <p className="text-gray-500 text-sm sm:text-base mt-1">
                {total} public beach access points in {displayCityName}, {displayStateName}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                variant="outline"
                onClick={handleShare}
                className="border-2 border-gray-200 hover:bg-gray-50 text-sm h-9 sm:h-10"
                size="sm"
              >
                <Share2 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Share</span>
              </Button>
              <Link to={`/map?state=${stateSlug}`}>
                <Button className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-semibold text-sm h-9 sm:h-10" size="sm">
                  <MapIcon className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">View Map</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Blue Divider */}
      <div className="h-2 bg-[#0EA5E9]" />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="grid lg:grid-cols-5 gap-6 sm:gap-8">
          {/* Left Column */}
          <div className="lg:col-span-3 space-y-6">
            {/* City Image */}
            <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-lg">
              <img
                src={getCityImage()}
                alt={`Beach in ${displayCityName}`}
                className="w-full h-[200px] sm:h-[300px] lg:h-[350px] object-cover"
              />
              <div className="absolute top-3 left-3 sm:top-4 sm:left-4">
                <span className="bg-[#10B981] text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg font-bold text-xs sm:text-sm shadow-lg">
                  🏖️ {total} Beach Access Points
                </span>
              </div>
            </div>

            {/* Overview */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 font-['Outfit'] mb-3 sm:mb-4">Overview</h2>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                Find {total} public beach access points in {displayCityName}, {displayStateName}. 
                Our directory provides detailed information about each beach entrance including 
                parking availability, restroom facilities, showers, and accessibility features. 
                Plan your perfect beach day with easy directions to every access point.
              </p>
            </div>

            {/* All Beaches */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 font-['Outfit'] mb-4">
                All Beach Access in {displayCityName}
              </h2>

              {loading ? (
                <div className="grid gap-3 sm:gap-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-28 sm:h-32 bg-gray-100 animate-pulse rounded-xl" />
                  ))}
                </div>
              ) : beaches.length === 0 ? (
                <div className="text-center py-8 sm:py-12 text-gray-500">
                  No beaches found in {displayCityName}.
                </div>
              ) : (
                <div className="grid gap-3 sm:gap-4">
                  {beaches.map((beach) => (
                    <BeachCard key={beach.id} beach={beach} />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 font-['Outfit'] mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <Link to={`/search?state=${stateSlug}`}>
                  <Button className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-semibold h-11 sm:h-12">
                    <Search className="w-5 h-5 mr-2" />
                    More {displayStateName} Beaches
                  </Button>
                </Link>
                <Link to={`/map?state=${stateSlug}`}>
                  <Button variant="outline" className="w-full border-2 border-gray-200 hover:bg-gray-50 h-11 sm:h-12">
                    <MapIcon className="w-5 h-5 mr-2" />
                    View on Map
                  </Button>
                </Link>
              </div>
            </div>

            {/* SEO Articles - All States */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-bold text-gray-800 font-['Outfit'] mb-4">
                  Beach Guides
                </h2>
                <div className="space-y-2">
                  <Link
                    to={`/${stateSlug}/best-beach-access/${citySlug}`}
                    className="flex items-center justify-between p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl hover:from-amber-100 hover:to-orange-100 transition-colors group"
                  >
                    <span className="font-medium text-gray-800">Best Beach Access</span>
                    <ChevronRight className="w-5 h-5 text-orange-500 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to={`/${stateSlug}/free-beach-access/${citySlug}`}
                    className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl hover:from-green-100 hover:to-emerald-100 transition-colors group"
                  >
                    <span className="font-medium text-gray-800">Free Beach Access</span>
                    <ChevronRight className="w-5 h-5 text-green-500 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to={`/${stateSlug}/beach-access-with-parking/${citySlug}`}
                    className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-sky-50 rounded-xl hover:from-blue-100 hover:to-sky-100 transition-colors group"
                  >
                    <span className="font-medium text-gray-800">Beach Access with Parking</span>
                    <ChevronRight className="w-5 h-5 text-blue-500 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>

            {/* Map */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 font-['Outfit'] mb-4">Location</h2>
              <div className="h-[250px] sm:h-[300px] rounded-xl overflow-hidden">
                {beaches.length > 0 ? (
                  <BeachMap beaches={beaches} />
                ) : (
                  <div className="h-full bg-gray-100 flex items-center justify-center text-gray-500">
                    Loading map...
                  </div>
                )}
              </div>
            </div>

            {/* Back to State */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 font-['Outfit'] mb-4">Explore More</h2>
              <Link
                to={`/state/${stateSlug}`}
                className="flex items-center justify-between p-3 sm:p-4 bg-[#DBEAFE] rounded-xl hover:bg-[#BFDBFE] transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-[#0EA5E9]" />
                  <span className="font-medium text-gray-800">All {displayStateName} Beaches</span>
                </div>
                <ChevronRight className="w-5 h-5 text-[#0EA5E9] group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-8 sm:mt-12 bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8">
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 font-['Outfit'] mb-4 sm:mb-6">
            Frequently Asked Questions
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">
                How many beach access points are in {displayCityName}?
              </h3>
              <p className="text-gray-600 text-sm">
                There are {total} public beach access {total === 1 ? "point" : "points"} in {displayCityName}, {displayStateName}.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">
                Are there free parking options?
              </h3>
              <p className="text-gray-600 text-sm">
                Many beaches offer free parking. Use our filters to find beaches with parking availability.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">
                How do I get directions?
              </h3>
              <p className="text-gray-600 text-sm">
                Click on any beach access point and use the "Get Directions" button to open navigation in Google Maps.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">
                Are beaches wheelchair accessible?
              </h3>
              <p className="text-gray-600 text-sm">
                Some beaches offer wheelchair accessibility. Look for the "Accessible" badge on beach listings.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CityPage;
